// Auditor magic-link landing (Session AEJ, Director decision D29). The office-generated access
// link lands here. It validates the token and, on success, issues the standard auditor session
// and sends the assessor to the read-only compliance portal. On any failure it shows a clean,
// branded page telling them to contact ELE for a new link -- never a login form (there is no
// password), and never distinguishing an expired link from an invalid one (no enumeration).
//
// Session ASB review, finding F3: a signed-in auditor whose SESSION ends (60 minutes idle, the 12-hour limit, or the
// deploy that ended every old session) is sent here as /auditor/access/invalid?reason=<why>. Their emailed link is
// multi-use within its window, so opening it again signs them straight back in: they are told that, not that their
// link has expired. The contact-ELE copy stays for a link that genuinely failed, a bare sentinel, and an ended access
// window (auditor_expired). The wording is true either way: if the window has closed, the link itself says so.
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { ApiError } from "../../lib/api";
import { Button } from "../../components/Button";

const PHONE = "0203 576 4760";
type State = "checking" | "expired" | "ended" | "network";

// The session-end reasons (sessionEvents.ts) that say the SESSION ended, not the access window.
const SESSION_ONLY_ENDED = new Set(["idle_timeout", "session_expired", "signed_out", "no_session"]);

export function AuditorAccess() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const { user, loading, auditorAccess } = useAuth();
  const navigate = useNavigate();
  // "invalid" is the sentinel the retired /auditor-login route redirects to: show the contact
  // page straight away without a pointless validate call. With a session-end ?reason (ProtectedRoute), it is a
  // session that ended rather than a link that failed.
  const isSentinel = !token || token === "invalid";
  const sessionOnlyEnded = isSentinel && SESSION_ONLY_ENDED.has(params.get("reason") ?? "");
  const [state, setState] = useState<State>(isSentinel ? (sessionOnlyEnded ? "ended" : "expired") : "checking");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (isSentinel) return;
    let cancelled = false;
    setState("checking");
    (async () => {
      try {
        await auditorAccess(token!);
        if (!cancelled) navigate("/auditor/compliance-reports", { replace: true });
      } catch (err) {
        if (cancelled) return;
        // Any 401 -> the SAME "expired / contact ELE" page: an invalid link and an expired one
        // are deliberately indistinguishable (no enumeration). A transient/network error is
        // separated so the auditor can retry rather than being told to phone ELE.
        setState(err instanceof ApiError && err.status === 401 ? "expired" : "network");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSentinel, token, auditorAccess, navigate, attempt]);

  // An already-signed-in auditor who lands on the sentinel (e.g. an old /auditor-login bookmark)
  // goes straight to their portal instead of the contact page.
  if (!loading && user?.role === "auditor" && isSentinel) {
    return <Navigate to="/auditor/compliance-reports" replace />;
  }

  return (
    // .auditor-ui scopes the Session AFK light/dark theme system to this page too (Session
    // AOR): it is the auditor's very first impression of the portal, before they are even
    // signed in, so it must render in the same light-by-default theme as the layout inside.
    // There is no ThemeToggle here (no shell chrome, and an unauthenticated visitor has not
    // proven who they are yet); it simply renders in whichever theme the pre-paint script
    // picked.
    <div className="auditor-ui flex min-h-dvh flex-col items-center justify-center bg-ele-base px-4 text-ele-body">
      <img
        src="/ELE_logo_new_reqt_FINALv2PNG.png"
        alt="East London Elevators"
        className="mb-6 block h-auto w-auto max-w-[200px]"
      />
      <div className="w-full max-w-sm rounded-ele border border-ele-purple-accent bg-ele-surface p-8 text-center">
        <h1 className="font-heading text-3xl tracking-wide text-ele-strong">Compliance Portal</h1>
        <p className="mb-6 mt-1 font-menu text-xs uppercase tracking-widest text-ele-blue">East London Elevators Ltd</p>

        {state === "checking" && (
          <p role="status" aria-live="polite" className="text-sm">
            Verifying your access link...
          </p>
        )}

        {state === "expired" && (
          <p role="alert" className="text-sm text-ele-strong">
            This access link has expired or is no longer valid. Please contact East London Elevators on {PHONE} to request a new one.
          </p>
        )}

        {state === "ended" && (
          <p role="alert" className="text-sm text-ele-strong">
            Your session has ended. Open the access link from your email again to continue. If that link has expired, please
            contact East London Elevators on {PHONE} for a new one.
          </p>
        )}

        {state === "network" && (
          <div>
            <p role="alert" className="mb-4 text-sm text-ele-strong">
              Unable to verify your access link right now. Please try again in a moment, or call us on {PHONE}.
            </p>
            <Button onClick={() => setAttempt((a) => a + 1)} className="uppercase">
              Try again
            </Button>
          </div>
        )}

        <p className="mt-6 border-t border-ele-purple-accent pt-4 font-menu text-xs text-ele-body">
          For access or help, call East London Elevators on {PHONE}.
        </p>
      </div>
    </div>
  );
}
